/**
 * Notification service — platform-agnostic business logic.
 *
 * Pure data/string operations only. Icon rendering and color classes are
 * web-only and live in apps/web/src/services/notification/NotificationIconService
 * (which extends this class to keep call-sites unchanged).
 */
import { formatAmountForCurrency } from '@splitfiesta/shared';
import type { NotificationResponseDto } from '@splitfiesta/shared';

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&nbsp;/g, ' ');
}

export class NotificationService {
  static getNotificationPath(
    notification: NotificationResponseDto
  ): string | null {
    const { type, params } = notification;

    switch (type) {
      case 'expense_created':
      case 'expense_updated':
      case 'expense_deleted':
      case 'expense_comment':
      case 'expense_reaction':
      case 'expense_recurring_created':
      case 'expense_pick_items':
        return params?.expenseId && params?.groupId
          ? `/groups/${params.groupId}/expenses/${params.expenseId}`
          : null;

      case 'group_created':
      case 'group_updated':
      case 'group_deleted':
      case 'group_member_joined':
      case 'group_member_left':
      case 'group_member_removed':
      case 'group_member_promoted':
      case 'group_member_demoted':
      case 'group_pro_added':
        return params?.groupId ? `/groups/${params.groupId}` : null;

      case 'expense_recurring_paused':
        return params?.groupId ? `/groups/${params.groupId}/recurring` : null;

      case 'placeholder_claim_requested':
      case 'placeholder_claim_confirmed':
      case 'placeholder_claim_rejected':
      case 'placeholder_claimed':
        return params?.groupId ? `/groups/${params.groupId}/members` : null;

      case 'settlement_created':
      case 'settlement_confirmed':
      case 'settlement_rejected':
      case 'settlement_cancelled':
        return params?.groupId ? `/groups/${params.groupId}/settlements` : null;

      case 'payment_reminder':
      case 'expense_reminder':
        return params?.groupId ? `/groups/${params.groupId}/balances` : null;

      case 'system':
        return null;

      default:
        return null;
    }
  }

  static formatNotificationMessage(
    notification: NotificationResponseDto,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    if (notification.messageKey) {
      try {
        const key = notification.messageKey.replace('notifications.', '');
        const params = { ...notification.params };

        if (!params.userName && key.startsWith('expense.')) {
          params.userName = 'Someone';
        }

        for (const paramKey of Object.keys(params)) {
          if (typeof params[paramKey] === 'string') {
            params[paramKey] = decodeHtmlEntities(params[paramKey] as string);
          }
        }

        if (
          params &&
          typeof params.amount === 'number' &&
          typeof params.currency === 'string'
        ) {
          const formattedAmount = formatAmountForCurrency(
            params.amount,
            params.currency
          );
          params.amount = `${formattedAmount} ${params.currency}`;
        }

        return t(key, params || {});
      } catch {
        return notification.messageKey;
      }
    }

    return t('unknown', notification.params || {});
  }

  static getNotificationPriority(type: string): 'high' | 'medium' | 'low' {
    switch (type) {
      case 'payment_reminder':
      case 'expense_reminder':
      case 'settlement_confirmed':
        return 'high';

      case 'expense_created':
      case 'expense_updated':
      case 'settlement_created':
      case 'group_created':
      case 'group_pro_added':
        return 'medium';

      case 'expense_comment':
      case 'expense_reaction':
      case 'group_member_joined':
      case 'group_member_left':
        return 'low';

      default:
        return 'medium';
    }
  }

  static groupNotificationsByDate(
    notifications: NotificationResponseDto[]
  ): Record<string, NotificationResponseDto[]> {
    const grouped: Record<string, NotificationResponseDto[]> = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach(notification => {
      const notificationDate = new Date(notification.createdAt);
      let dateKey: string;

      if (notificationDate.toDateString() === today.toDateString()) {
        dateKey = 'today';
      } else if (notificationDate.toDateString() === yesterday.toDateString()) {
        dateKey = 'yesterday';
      } else {
        dateKey = notificationDate.toISOString().split('T')[0];
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(notification);
    });

    return grouped;
  }

  static filterNotificationsByType(
    notifications: NotificationResponseDto[],
    types: string[]
  ): NotificationResponseDto[] {
    return notifications.filter(notification =>
      types.includes(notification.type)
    );
  }

  static requiresImmediateAttention(
    notification: NotificationResponseDto
  ): boolean {
    const highPriorityTypes = [
      'payment_reminder',
      'expense_reminder',
      'settlement_confirmed',
    ];

    return (
      highPriorityTypes.includes(notification.type) && !notification.isRead
    );
  }

  static calculateNotificationStats(notifications: NotificationResponseDto[]): {
    total: number;
    unread: number;
    byType: Record<string, number>;
    highPriority: number;
  } {
    const stats = {
      total: notifications.length,
      unread: 0,
      byType: {} as Record<string, number>,
      highPriority: 0,
    };

    notifications.forEach(notification => {
      if (!notification.isRead) {
        stats.unread++;
      }

      if (!stats.byType[notification.type]) {
        stats.byType[notification.type] = 0;
      }
      stats.byType[notification.type]++;

      if (this.requiresImmediateAttention(notification)) {
        stats.highPriority++;
      }
    });

    return stats;
  }
}
